import { ActionContext } from 'vuex'
import { getTenantPrivileges } from '@/controllers/global'
import { loadTenantProperties } from '@/controllers/admin'
import { resolveMinimumPasswordLength } from '@/helpers/passwordPolicy'

const SET_TENANT_ACCESS_PRIVILEGES = 'SET_TENANT_ACCESS_PRIVILEGES'
const SET_TENANT_PROPERTIES = 'SET_TENANT_PROPERTIES'
const TENANT_PROPERTIES_LOADING = 'TENANT_PROPERTIES_LOADING'
const TENANT_PROPERTIES_FAILED = 'TENANT_PROPERTIES_FAILED'

// Interfaces and Types
interface TenantProps {
  [key: string]: string
}
interface TenantState {
    tenantAccessPrivileges: string[],
    tenantProperties: TenantProps,
    tenantPropertiesLoaded: boolean,
    tenantPropertiesLoadError: boolean
}
type Context = ActionContext<TenantState, any>

// Store module
const state = {
  tenantAccessPrivileges: [],
  tenantProperties: {},
  tenantPropertiesLoaded: false,
  tenantPropertiesLoadError: false
}

const getters = {
  /*
     * This check examines the tenant-level API_KEY_ADMIN permission is set
     * and, correspondingly, whether the user can provision the user group-level
     * API_KEY_ADMIN permission stored in the user module. If true, the user
     * will see the "API Key Admin" checbox in Admin UserGroups and be able to update it.
     */
  hasTenantAPIKeyAdmin (state: TenantState): boolean {
    return state.tenantAccessPrivileges.includes('API_KEY_ADMIN')
  },
  hasEnabledWidgets(state: TenantState): boolean {
    return state.tenantAccessPrivileges.includes('WIDGET_KEY_ADMIN')
  },
  hasEnabledWidgetLogo(state: TenantState): boolean {
    return state.tenantProperties.ENABLE_DOCUMOTO_WIDGET_LOGO === 'true'
  },
  hasTenantProperties(state: TenantState): boolean {
    return !!Object.keys(state.tenantProperties).length
  },
  hasEnabledIframeSupport(state: TenantState) {
    return state.tenantProperties['ENABLE_IFRAME_SUPPORT'] === "true"
  },
  hasDisabledBanner(state: TenantState) {
    return state.tenantProperties["DISABLE_LIBRARY_BANNER_IMAGE"] === "true"
  },
  displaySupplierPartInfo(state: TenantState) {
    return state.tenantProperties["DISPLAY_SUPPLIER_PART_INFO"] === "true"
  },
  library3dEnableExplode (state: TenantState): boolean {
    return state.tenantProperties.LIBRARY_3D_ENABLE_EXPLODE === 'true'
  },
  library3dShowAdvancedSelectionControls (state: TenantState): boolean {
    return state.tenantProperties.LIBRARY_3D_SHOW_ADVANCED_SELECTION_CONTROLS === 'true'
  },
  useMediaCategoryContextualSearch(state: TenantState) {
    return state.tenantProperties["ENABLE_MEDIA_CATEGORY_CONTEXTUAL_SEARCH"] === "true"
  },
  autoOpenPartThumbnailPreviewEnabled(state: TenantState): boolean {
    const raw = state.tenantProperties["AUTO_OPEN_PART_THUMBNAIL_PREVIEW"]
    return raw === undefined ? true : raw === "true"
  },
  minimumPasswordLength (state: TenantState): number {
    return resolveMinimumPasswordLength(state.tenantProperties.MINIMUM_PASSWORD_LENGTH)
  },
  tenantPropertiesLoaded (state: TenantState): boolean {
    return state.tenantPropertiesLoaded
  },
  tenantPropertiesLoadError (state: TenantState): boolean {
    return state.tenantPropertiesLoadError
  }
}

const actions = {
  async loadTenantPrivileges (context: Context, tenantKey: string): Promise<void> {
    try {
      const tenantAccessPrivileges = await getTenantPrivileges(tenantKey)
      context.commit('SET_TENANT_ACCESS_PRIVILEGES', tenantAccessPrivileges)
    } catch (error) {
      console.log('error :>> ', error)
    }
  },
  async loadTenantProperties (context: Context, tenantKey: string = context.rootState.user.tenantKey) {
    context.commit(TENANT_PROPERTIES_LOADING)
    try {
      const tenantProps = await loadTenantProperties(tenantKey)
      context.commit(SET_TENANT_PROPERTIES, tenantProps)
    } catch(e) {
      context.commit(TENANT_PROPERTIES_FAILED)
      console.error(e)
    }
  }
}

const mutations = {
  [SET_TENANT_ACCESS_PRIVILEGES] (state: TenantState, payload: string[]) {
    state.tenantAccessPrivileges = payload
  },
  [SET_TENANT_PROPERTIES] (state: TenantState, payload: TenantProps) {
    state.tenantProperties = payload
    state.tenantPropertiesLoaded = true
    state.tenantPropertiesLoadError = false
  },
  [TENANT_PROPERTIES_LOADING] (state: TenantState) {
    state.tenantPropertiesLoaded = false
    state.tenantPropertiesLoadError = false
  },
  [TENANT_PROPERTIES_FAILED] (state: TenantState) {
    state.tenantPropertiesLoaded = false
    state.tenantPropertiesLoadError = true
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
